import { useAuth } from 'context/AuthContext';
import { useShoppingCart } from 'context/ShoppingCartContext';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Heart, Menu, ShoppingCart, User, X } from 'lucide-react';
import { cn } from 'ui/lib/utils';

import Logo from './marketplace/Logo';
import SearchBar from './marketplace/SearchBar';
import IconButton from './marketplace/IconButton';
import TopBar from './marketplace/TopBar';
import CategoryNav from './marketplace/CategoryNav';

const UserMenu = dynamic(() => import('@c/menus/UserMenu'), { ssr: false });

interface NavBarProps {
  showCloseButton: boolean;
  onActionButtonClicked?: () => void;
}

const Navbar = ({ showCloseButton, onActionButtonClicked }: NavBarProps) => {
  const { user, userDoc } = useAuth();
  const { getItemCount, setCartOpen } = useShoppingCart();
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  // GB #1401 (bug B): the header search box loses the typed query once the
  // results page renders — it always mounted empty regardless of the URL.
  // Seed it from the current keyword so both the desktop and mobile search
  // rows reflect what was actually searched. Only meaningful on /search;
  // every other route has no `keyword` param and this stays ''.
  const currentSearchKeyword =
    typeof router.query.keyword === 'string' ? router.query.keyword : '';

  // Hysteresis so the collapse doesn't bounce. Collapsing removes ~54px of
  // header height; crossing a single threshold lets the browser's scroll
  // anchoring nudge scrollY back over it, which re-expands, re-collapses… A
  // deadband wider than the collapse height (collapse at 96, expand at 24)
  // breaks the loop: the nudge can't cross from one threshold back to the other.
  useEffect(() => {
    const COLLAPSE_AT = 96;
    const EXPAND_AT = 24;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled((prev) => {
        if (!prev && y > COLLAPSE_AT) return true;
        if (prev && y < EXPAND_AT) return false;
        return prev;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Publish the sticky header's live height as --gf-header-h so anything that
  // pins below it (e.g. the category filter bar) stays flush without hardcoding
  // a number that breaks whenever this header changes. Tracks the scroll
  // collapse animation and the responsive CategoryNav (xl-only).
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const publishHeight = () =>
      document.documentElement.style.setProperty(
        '--gf-header-h',
        `${el.offsetHeight}px`
      );
    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(el);
    return () => {
      observer.disconnect();
      // Remove rather than leave the last measured value behind: this header
      // does not render on every layout (checkout, onboarding), and a stale
      // inline value would keep overriding the responsive default in
      // globals.css and pin content below a header that is no longer there.
      document.documentElement.style.removeProperty('--gf-header-h');
    };
  }, []);

  const itemCount = getItemCount();
  const sellHref = userDoc?.account_activated ? '/sell/create-listing' : '/sell';

  return (
    <div ref={headerRef} className="sticky top-0 z-sticky w-full">
      {/* Dark utility bar — fades up + collapses on scroll, but only from md up.
          On mobile the height stays fixed: animating it during scroll shifts
          layout, and scroll-driven shift counts toward CLS (measured 0.127 on
          mobile). Frozen height = no shift. */}
      <div
        className={cn(
          'overflow-hidden transition-[height] duration-300 ease-out',
          scrolled ? 'h-[28px] md:h-0' : 'h-[28px] md:h-[36px]'
        )}
      >
        <div
          className={cn(
            'transition-[opacity,transform] duration-300 ease-out',
            scrolled
              ? 'opacity-100 md:-translate-y-full md:opacity-0'
              : 'translate-y-0 opacity-100'
          )}
        >
          <TopBar />
        </div>
      </div>

      {/* Main bar — stays sticky, compacts on scroll */}
      <header
        data-slot="header"
        data-scrolled={scrolled}
        className={cn(
          'w-full border-b border-border bg-white transition-shadow',
          scrolled && 'shadow-md'
        )}
      >
        <div
          className={cn(
            // Compact-on-scroll only from md up — same CLS reason as the utility
            // bar; mobile keeps a constant 68px. Tighter gap on mobile buys back
            // the width that pushed the row to 433px (overflow) at 390px.
            // 68px on mobile (not 80px): 28 + 68 + 40 + 8 = today's 144px
            // header, per Bill's "no 25px increase" ruling. Still clears the
            // 44px tap-target slots inside it.
            'mx-auto flex w-full max-w-default items-center gap-[8px] px-4 transition-[height] duration-300 ease-out sm:gap-[12px] md:gap-[20px] lg:px-[40px] xl:px-default-padding-x',
            scrolled ? 'h-[68px] md:h-[62px]' : 'h-[68px] md:h-[80px]'
          )}
        >
          <button
            type="button"
            // "menu-trigger" was a developer string read aloud verbatim.
            aria-label={showCloseButton ? 'Close menu' : 'Open menu'}
            aria-expanded={showCloseButton}
            onClick={onActionButtonClicked}
            className="inline-flex size-[44px] shrink-0 items-center justify-center text-dark xl:hidden"
          >
            {showCloseButton ? (
              <X aria-hidden="true" size={24} />
            ) : (
              <Menu aria-hidden="true" size={24} />
            )}
          </button>

          <Link
            href="/"
            title="GearFocus, Your New And Used Camera Marketplace"
            aria-label="GearFocus home"
            // Shrinkable, not shrink-0: the logo is the one item in this row
            // that can give up width without breaking a tap target, so it is
            // the overflow valve. Without this the row had no give at all and
            // a signed-in phone got a sideways-scrolling page.
            className="min-w-0 shrink transition-opacity hover:opacity-85"
          >
            {/* 22px below sm (115px wide) instead of 28px (146px). The
                signed-in row is the width-critical case: hamburger 44 + logo +
                avatar 44 + cart 44 + "Sell", and 146px of unshrinkable logo put
                that past the content box at 390px.

                Measured in Chromium against the built app, signed in (the
                wishlist is hidden below sm, so signing in adds exactly one
                44px slot at these widths):

                  414px — 78px slack, logo full size
                  390px — 54px slack, logo full size
                  360px — 24px slack, logo full size
                  359px — 84px slack, logo full size  (Sell CTA drops out)
                  320px — 45px slack, logo full size  (Sell CTA drops out)

                No horizontal page overflow at any of them, and the wordmark
                renders at full size throughout. Below 360px the row cannot hold
                the Sell CTA as well, so that is what gives way (see the Link
                further down) — everything else here is either the logo or a
                44px tap target. The shrink valve on this Link stays as a
                backstop for anything narrower than we test. */}
            <Logo height={28} mobileHeight={22} />
          </Link>

          <div className="hidden min-w-0 flex-1 md:block md:max-w-[500px]">
            <SearchBar variant="header" defaultValue={currentSearchKeyword} />
          </div>

          <div className="ml-auto flex shrink-0 items-center gap-[8px]">
            {/* Visible at EVERY width. This was `hidden … sm:inline-flex`, which
                left phone visitors with no sign-in control at all in the header
                — production shows a login icon at every width. One element with
                responsive content rather than two links to the same href: an
                icon in a 44px tap target below sm, the text label from sm up. */}
            {!user && (
              <Link
                href="/login"
                aria-label="Sign in"
                title="Sign in"
                // -8.6px right margin below sm only (Bill, 2026-08-22): the
                // guest icon and cart icon are both centered in a 56px box, so
                // their glyph-to-glyph gap was flex gap-[8px] + both boxes'
                // unused side padding (12.6px each) = 33.2px — visibly wider
                // than the cart-to-Sell-button gap (12.6px + 8px + Sell's own
                // ml-[4px] = 24.6px). This margin closes that difference so
                // both gaps read the same, by pulling the man toward the cart
                // rather than pushing the cart/Sell (which were already right).
                className="flex size-[56px] shrink-0 items-center justify-center text-[14px] font-medium text-gray-700 transition-colors hover:text-primary -mr-[8.6px] sm:mr-0 sm:h-[40px] sm:w-auto sm:px-[12px]"
              >
                {/* 30.8px below sm to match the cart icon's mobile glyph size
                    (Bill, 2026-08-22 — the guest sign-in icon read small next
                    to the enlarged cart icon). Cart's own 30.8px is itself
                    175% of its 17.6px desktop size (Bill, 2026-08-21). */}
                <User aria-hidden="true" size={22} className="size-[30.8px] sm:hidden" />
                <span className="hidden whitespace-nowrap sm:inline">
                  Sign in
                </span>
              </Link>
            )}

            {user && (
              <div className="flex size-[44px] shrink-0 items-center justify-center">
                <UserMenu />
              </div>
            )}

            {/* Signed-in only, deliberately (Kyle, Aug 2026). A logged-out
                wishlist entry point led to a broken flow (Arctic Leaf AL-32);
                rather than fix that flow, the entry point is gone. Recorded
                here so it reads as a decision rather than an oversight — the
                previous version looked identical but nobody had chosen it.

                Hidden below sm as well: it was the fourth 44px slot in a row
                that overflowed 390px when signed in. The account menu carries
                the link on phones (useMenuItems accountItems). */}
            {user && (
              <Link
                href="/wishlist"
                aria-label="Wishlist"
                className="hidden size-[44px] shrink-0 items-center justify-center sm:flex"
              >
                <IconButton
                  icon={Heart}
                  label="Wishlist"
                  size="avatar"
                  styleVariant="circle"
                />
              </Link>
            )}

            {/* 44px slot keeps the cart's spacing even with the avatar/wishlist,
                while the button itself matches the avatar's 32px circle — on
                desktop (sm+). Below sm (Bill, 2026-08-21): no circle, icon at
                175% (56px box / 30.8px glyph) — the slot grows to match so the
                bigger icon isn't clipped. */}
            <div className="flex size-[56px] shrink-0 items-center justify-center sm:size-[44px]">
              <IconButton
                icon={ShoppingCart}
                label="Cart"
                size="avatar"
                styleVariant="circle"
                badge={itemCount}
                onClick={() => setCartOpen((prev) => !prev)}
                className={cn(
                  'size-[56px] rounded-none border-0 bg-transparent [&_svg]:size-[30.8px]',
                  'sm:size-[32px] sm:rounded-full sm:border sm:border-gray-100 sm:bg-white sm:[&_svg]:size-[17.6px]',
                  // Keep the 32px circle's tappable area extended to the 44px
                  // floor on desktop; below sm the box itself already exceeds
                  // that floor, so the extra inset is just a harmless margin.
                  "after:absolute after:-inset-[6px] after:content-['']"
                )}
              />
            </div>

            <Link
              href={sellHref}
              title="Sell Your Used Camera Gear With GearFocus"
              // Hidden below xs (360px). At 320px the signed-in row cannot hold
              // this and a full-size wordmark, and everything else in the row is
              // either the logo or a 44px tap target. Dropping it there is a
              // no-op against what ships today — production's mobile header has
              // no Sell CTA at any width — and the path survives in the menu
              // drawer for both auth states (signed out: the "Sell your gear"
              // button; signed in: DashboardSideMenu's Sell section).
              //
              // Short label on mobile ("Sell") so the CTA can't clip to
              // "Sell your gea" at 390px; full label + arrow from sm up.
              // White on the brand orange (#C75008) is 4.59:1 — clears WCAG AA
              // for this 14px semibold label. The trade-off this comment used
              // to record (2.89:1 on the old #FF6900, accepted as a brand call)
              // no longer exists: the brand orange itself was darkened.
              className="ml-[4px] hidden h-[40px] items-center gap-[6px] whitespace-nowrap rounded-[10px] bg-primary px-[12px] text-[14px] font-semibold text-white transition-colors hover:bg-primary-hover xs:inline-flex sm:px-[16px]"
            >
              <span className="sm:hidden">Sell</span>
              <span className="hidden sm:inline">Sell your gear</span>
              <ArrowRight aria-hidden="true" size={16} className="hidden sm:block" />
            </Link>
          </div>
        </div>

        {/* Mobile search row (gearfocus-web keeps search accessible on mobile) */}
        <div className="bg-white px-4 pb-[8px] md:hidden">
          <SearchBar variant="header" defaultValue={currentSearchKeyword} />
        </div>
      </header>

      <CategoryNav />
    </div>
  );
};

export default Navbar;
